import { Form, Formik, FormikHelpers } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Portal } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';

import { transformProfileObjectToRegisterFields } from './utils';
import { handleServerErrors, toGregorian } from 'utils/utils';
import AnimatedLayout from 'components/layouts/animated-layout';
import { SignatureSchema } from '../signature.form';
import { ContactSchema } from '../contact.form';
import { AddressSchema } from '../address.form';
import { IdentitySchema } from '../identity.form';
import { LegalSchema } from '../legal.form';
import ProfileFormTemplate from './profile-form.template';
import { UpdateUser } from 'features/profile/types/user.types';
import {
    useAddressesQuery,
    useUpdateProfileMutation,
    useUserProfileQuery,
} from 'app/store';
import { useMemo, useState } from 'react';

export const ProfileForm = (): JSX.Element => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState('person');

    const {
        data: profile,
        isLoading: isFetchingProfile,
        refetch: refetchProfile,
    } = useUserProfileQuery();

    const {
        data: addresses,
        isLoading: isFetchingAddress,
        refetch: refetchAddresses,
    } = useAddressesQuery(undefined, { refetchOnMountOrArgChange: true });

    const [updateProfile, { isLoading }] = useUpdateProfileMutation();

    const handleOnSubmit = async (
        values: UpdateUser,
        { setSubmitting, setStatus }: FormikHelpers<UpdateUser>,
    ) => {
        try {
            const _values: UpdateUser = {
                ...values,
                birth: toGregorian(values.birth),
            };

            await updateProfile(_values).unwrap();

            // Ensure the wizard cache contains the values that were just saved
            // before moving to the next step. This prevents stale profile/address
            // values from reappearing when the user comes back without a reload.
            await Promise.all([refetchProfile(), refetchAddresses()]);

            navigate('/register/bank');
        } catch (error: any) {
            handleServerErrors(error, setStatus);
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues = useMemo(
        () => transformProfileObjectToRegisterFields(profile, addresses?.items),
        [profile, addresses, isFetchingProfile, isFetchingAddress],
    );

    const profileFormSchema = useMemo(() => {
        // Registration does not collect a tax code in the address section for
        // either personal or legal accounts. Tax data is handled later where
        // it is actually needed.
        const registrationAddressSchema = AddressSchema.shape({
            taxcode: Yup.string().nullable().notRequired(),
        });

        return Yup.object()
            .shape({
                signatures: Yup.array().of(SignatureSchema),
            })
            .concat(
                userType === 'person'
                    ? IdentitySchema
                    : userType === 'company'
                      ? LegalSchema
                      : Yup.object(),
            )
            .concat(ContactSchema)
            .concat(registrationAddressSchema);
    }, [userType]);

    return (
        <AnimatedLayout>
            <Formik
                key={profile?.id}
                enableReinitialize
                initialValues={initialValues}
                validationSchema={profileFormSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={handleOnSubmit}>
                {(props) => (
                    <Form
                        id="register-profile-form"
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true">
                        <input
                            type="text"
                            name="register-autofill-trap"
                            autoComplete="new-password"
                            tabIndex={-1}
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                opacity: 0,
                                pointerEvents: 'none',
                                width: 1,
                                height: 1,
                            }}
                        />
                        <ProfileFormTemplate
                            props={{ ...props, setUserType, registration: true }}
                        />

                        <Portal
                            container={() =>
                                document.getElementById(
                                    'register-profile-header-action',
                                )
                            }>
                            <LoadingButton
                                loading={
                                    isLoading ||
                                    isFetchingProfile ||
                                    isFetchingAddress
                                }
                                variant="contained"
                                color="primary"
                                disabled={props.isSubmitting}
                                endIcon={
                                    <i
                                        className="fa-light fa-arrow-left"
                                        style={{ fontSize: 13 }}
                                    />
                                }
                                type="submit"
                                form="register-profile-form">
                                تایید و ادامه
                            </LoadingButton>
                        </Portal>
                    </Form>
                )}
            </Formik>
        </AnimatedLayout>
    );
};
